import { gebi } from '../utils/domUtils.js';

const staticEffects = {
  // horizontalScrollContainer: horizontalScrollContainerEffect
};

const dynamicEffects = {
  ad: async () => import('../effects/adEffect.js'),
  animatedImage: async () => import('../effects/animatedImageEffect.js'),
  authorTemplate: async () => import('../effects/authorTemplateEffect.js'),
  blankTemplateContent: async () =>
    import('../effects/blankTemplateContentEffect.js'),
  conditionalRender: async () =>
    import('../effects/conditionalRenderEffect.js'),
  coverFlowWrapper: async () => import('../effects/coverFlowWrapperEffect.js'),
  expandedGallery: async () => import('../effects/expandedGalleryEffect.js'),
  horizontalScrollContainer: async () =>
    import('../effects/horizontalScrollContainerEffect.js'),
  hubPage: async () => import('../effects/hubPageEffect.js'),
  inlineSearch: async () => import('../effects/inlineSearchEffect.js'),
  mainNavigation: async () => import('../effects/mainNavigationEffect.js'),
  mostPopularContent: async () =>
    import('../effects/mostPopularContentEffect.js'),
  notFound: async () => import('../effects/notFoundEffect.js'),
  nowOnTv: async () => import('../effects/nowOnTvEffect.js'),
  photographerTemplate: async () =>
    import('../effects/photographerTemplateEffect.js'),
  searchForm: async () => import('../effects/searchFormEffect.js'),
  shareKit: async () => import('../effects/shareKitEffect.js'),
  showCard: async () => import('../effects/showCardEffect.js'),
  tvGuide: async () => import('../effects/tvGuideEffect.js'),
  verticalSlideshow: async () =>
    import('../effects/verticalSlideshowEffect.js'),
  videoModule: async () => import('../effects/videoModuleEffect.js'),
  videoPlayer: async () => import('../effects/videoPlayerEffect.js')
};

// For dynamic imported modules, a "mainNavigation" effect,
// dispatcher expects to find a exported function named "mainNavigationEffect".
const dispatcher = ({ name, params }) => {
  if (staticEffects[name]) {
    staticEffects[name](params);
  } else if (dynamicEffects[name]) {
    dynamicEffects[name]().then(effect => effect[`${name}Effect`](params));
  }
};

const targets = { window, document };
const getTarget = name => targets[name] || document.querySelector(name);

const controller = ({ name, params, condition, on }) => {
  // oxlint-disable-next-line no-new-func
  if (condition && Function(`return ${condition}`)()) {
    dispatcher({ name, params });
  } else if (on?.types?.length > 0) {
    const target = getTarget(on.target) || window;
    const listener = () => {
      // oxlint-disable-next-line no-new-func
      if (!on.condition || Function(`return ${on.condition}`)()) {
        dispatcher({ name, params });
      }
      // If more than one event triggers this effect, remove all of them.
      // When there is only one event, it gets auto removed by its "once" option
      if (on?.types?.length > 1) {
        on.types.forEach(type => target.removeEventListener(type, listener));
      }
    };
    on.types.forEach(type => {
      if (window.natgeo.events[type]) {
        listener();
      } else {
        target.addEventListener(type, listener, {
          once: true,
          passive: on.hasOwnProperty('passive') ? on.passive : true
        });
      }
    });
  } else {
    dispatcher({ name, params });
  }
};

const alreadyExecuted = new Set();
const observeFn = ({ name, params, observe, condition, on, once = true }) => {
  // Avoid executing the same effect twice.
  const signature = JSON.stringify({ name, params, observe, condition, on });
  if (once) {
    if (alreadyExecuted.has(signature)) {
      return;
    }
    alreadyExecuted.add(signature);
  }

  const observeTarget = observe ? gebi(observe) : null;
  if (observeTarget) {
    const observeCallback = (entries, observer) => {
      if (entries[0].isIntersecting || entries[0].isVisible) {
        observer.disconnect();
        controller({ name, params, condition, on });
      }
    };

    const observer = new IntersectionObserver(observeCallback, {
      rootMargin: '500px 0px 500px 0px'
    });
    observer.observe(observeTarget);
  } else {
    controller({ name, params, condition, on });
  }
};

export const effectService = {
  init: () => {
    // Process and replace queue
    window.natgeo.effects.forEach(effect => observeFn(effect));
    window.natgeo.effects.push = observeFn;
  }
};
